'use client';

import { type ChangeEvent, startTransition, useCallback, useRef } from 'react';
import { useFocus } from '@react-aria/interactions';
import { useRouter } from '@react-aria/utils';
import type { FocusableElement, Key } from '@react-types/shared';
import { stackKeyboardDelegate } from '../layouts/stack/stack-keyboard-delegate';
import type { ListFilterState } from './types';

/**
 * @description
 * A hook that provides search functionality for a filtered list component.
 * This hook creates handlers for:
 * - Input focus management
 * - Query state updates
 * - Keyboard navigation (arrow keys, enter)
 * - Selection management
 * - Cursor position preservation
 *
 * @example
 * ```tsx
 * const { inputProps, selectionCache } = useFilteredListSearch(listState);
 *
 * return ( <InputComponent {...inputProps} /> );
 * ```
 */
export function useFilteredListSearch<T>(state: ListFilterState<T>) {
  const { dispatchQuery, selectionManager: manager, queryState } = state;

  const router = useRouter();

  /**
   * We only allow selection of our list when focusing our input
   * if we are in single selection mode and selection behavior is replace.
   * Otherwise we treat the input as a regular input, and the user
   * must tab into the list for keyboard controls.
   */
  const enableComboboxStyleInteraction =
    manager.selectionMode === 'single' &&
    manager.selectionBehavior === 'replace' &&
    !state.isLoadingVisible;

  /**
   * Cache for storing input selection range (cursor position) to restore it after re-renders.
   * Ensures `start` and `end` are always numbers.
   */
  const selectionCache = useRef<{ start: number; end: number; value: string }>({
    start: 0,
    end: 0,
    value: queryState.query ?? '',
  });

  /**
   * Caches the input's selection range and value on each change event.
   * This is crucial for restoring the cursor position after re-renders,
   * for example, when the query state updates after a debounce.
   */
  const handleSearchInputChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      // The event target may not be available in async operations.
      if (!evt.target) return;
      if (evt.target.value === selectionCache.current.value) return;

      const { value, selectionStart, selectionEnd } = evt.target;
      selectionCache.current = {
        value,
        start: selectionStart ?? value.length,
        end: selectionEnd ?? value.length,
      };
    },
    []
  );

  /**
   * If the input is focused, update the state manager
   */
  const { focusProps } = useFocus({
    onFocusChange() {
      dispatchQuery({ type: 'toggle-focus' });
    },
  });

  /**
   * When the query changes, dispatch a query change event to the state manager
   */
  const handleQueryChange = useCallback(
    (value: string) => {
      startTransition(() => {
        dispatchQuery({ type: 'query', state: value });
      });
    },
    [dispatchQuery]
  );

  /**
   * Handle keyboard navigation for the input element
   */
  const handleFilteredListKeyboardNavigation = useCallback(
    (e: React.KeyboardEvent<FocusableElement>) => {
      const delegate = stackKeyboardDelegate(state);
      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          const nextKey =
            manager.focusedKey != null
              ? delegate?.getKeyBelow?.(manager.focusedKey)
              : delegate?.getFirstKey?.();
          selectKey(nextKey);
          break;
        }

        case 'ArrowUp': {
          e.preventDefault();
          const prevKey =
            manager.focusedKey != null
              ? delegate?.getKeyAbove?.(manager.focusedKey)
              : delegate?.getLastKey?.();
          selectKey(prevKey);
          break;
        }

        case 'Enter': {
          e.preventDefault();
          if (
            manager.focusedKey != null &&
            manager.isLink(manager.focusedKey ?? null)
          ) {
            const key = manager.focusedKey.toString();
            // this is crappy, and probably not necessary with
            // react-aria's (yet to be published) routing
            // update
            const element = document.querySelector(
              `[data-key="${CSS.escape(key)}"]`
            );
            const item = state.collection.getItem(manager.focusedKey);
            if (element) {
              router.open(
                element,
                e,
                item?.props?.href,
                item?.props?.routerOptions
              );
            }
          }
          break;
        }
        default:
      }

      /**
       * Helper function to safely select a key if it exists
       * This is used to select a key when the user presses the arrow keys or the enter key
       */
      function selectKey(newKey?: Key | null) {
        // loose nullish check is important if new key is also undefined
        if (state.isLoadingVisible || newKey == null) return;
        manager.setFocusedKey(newKey);
        if (enableComboboxStyleInteraction) {
          manager.replaceSelection(newKey);
        }
      }
    },
    [state, manager, router, enableComboboxStyleInteraction]
  );

  return {
    /**
     * The input props for the search field
     */
    inputProps: {
      ...focusProps,
      'aria-controls': state.id,
      onChange: handleSearchInputChange,
      onKeyDown: handleFilteredListKeyboardNavigation,
      onQueryChange: handleQueryChange,
      query: queryState.query,
    },
    /**
     * The selection cache for the input element
     */
    selectionCache,
  };
}
